/*
 * Header navigation — Spacemilk redesign.
 * Figma: 1323:17462 (desktop panel), 1323:17528 / 1323:17598 (mobile drawer).
 *
 * Loaded by sections/header-pill.liquid AFTER global.js, which it extends.
 */

/*
 * Dawn's MenuDrawer already gives us Escape, the focus trap, focus-out close,
 * the body scroll lock and the 400ms close animation, and none of that is worth
 * rewriting. It is reused wholesale — with one correction.
 *
 * MenuDrawer.bindEvents() binds EVERY <button> inside the drawer to
 * onCloseButtonClick, which closes a submenu. Our drawer has a ✕ in its own top
 * bar, and the frame puts it at level 1 as well as level 2. Left alone, tapping
 * it at level 1 runs closeSubmenu() against the main <details>: the drawer
 * appears to close, but closeMenuDrawer() never runs, so
 * body.overflow-hidden-tablet is never removed and the page underneath stays
 * unscrollable with no visible cause.
 *
 * So: exclude it from the close-submenu binding and close the drawer properly.
 * The back button inside a pane keeps Dawn's behaviour, which is correct for it.
 */
if (typeof HeaderDrawer !== 'undefined') {
  class HeaderNavDrawer extends HeaderDrawer {
    bindEvents() {
      this.querySelectorAll('summary').forEach((summary) =>
        summary.addEventListener('click', this.onSummaryClick.bind(this))
      );

      this.querySelectorAll('button:not(.hn-drawer__close)').forEach((button) =>
        button.addEventListener('click', this.onCloseButtonClick.bind(this))
      );

      this.querySelectorAll('.hn-drawer__close').forEach((button) =>
        button.addEventListener('click', (event) =>
          // closeMenuDrawer() returns early without an element to focus, so the
          // trigger has to be handed back explicitly or the drawer never closes.
          this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
        )
      );
    }

    /*
     * A branch row carries two targets: the collection name goes to the PLP,
     * everything else in the row opens level 2.
     *
     * The name is an <a> inside the <summary>, so a click on it has TWO default
     * actions queued — follow the link, and toggle the disclosure. preventDefault
     * cancels both; there is no way to keep one and drop the other. So the
     * navigation is re-issued by hand. Without this the pane slides in on the
     * way out of the page, which on a slow connection is exactly the flash of
     * the wrong screen the split is meant to avoid.
     *
     * Modified clicks are left alone so open-in-new-tab still works. Middle
     * clicks never reach here — modern browsers fire auxclick for those.
     */
    onSummaryClick(event) {
      const link = event.target.closest('.hn-drawer__row-link');

      if (link) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        window.location.assign(link.href);
        return;
      }

      super.onSummaryClick(event);
    }

    /*
     * Dawn resets aria-expanded on close only for a KeyboardEvent
     * (global.js: `if (event instanceof KeyboardEvent)`), but base.css hangs the
     * full-screen dark scrim off the attribute, not off [open]:
     *
     *   .header__icon--menu[aria-expanded='true']::before { ... }
     *
     * Close the drawer with a tap or a click and the scrim therefore survives
     * it. It is a pseudo-element of the <summary>, so it is not just a stuck
     * dimming: the next click anywhere on the page hit-tests to the summary and
     * re-opens the drawer. Measured at 375x702 with the drawer shut.
     *
     * Every close path — the ✕, the summary, Escape, submenus — funnels through
     * here, so one reset covers all of them.
     */
    closeMenuDrawer(event, elementToFocus) {
      // Mirrors HeaderDrawer's own first line. It bails on a missing
      // elementToFocus and leaves the drawer open — onFocusOut calls it with no
      // arguments at all — and clearing aria-expanded on a drawer that is still
      // open would drop the scrim under a live drawer. Deliberately duplicated
      // rather than inferred from .menu-opening, which openMenuDrawer adds in a
      // setTimeout and so is absent for a beat after the drawer opens.
      if (!elementToFocus) return;

      super.closeMenuDrawer(event, elementToFocus);

      // Nested summaries too: super clears [open] on every one of them, so
      // leaving them claiming aria-expanded="true" would be a lie as well.
      this.mainDetailsToggle
        .querySelectorAll('summary')
        .forEach((summary) => summary.setAttribute('aria-expanded', 'false'));
    }
  }

  customElements.define('header-nav-drawer', HeaderNavDrawer);
}

/*
 * Desktop: open the panel on hover.
 *
 * <header-menu> (Dawn's, assets/details-disclosure.js) opens on click only.
 * This subclass keeps every bit of that — the focus-out close, the
 * --header-bottom-position-desktop measurement, the click toggle for keyboard
 * and touch — and layers pointer intent on top.
 *
 * Two details that are not optional:
 *
 * 1. The panel is absolutely positioned at the wrapper's bottom edge, so there
 *    is a dead strip between the bottom of the summary and the top of the
 *    panel. It is a DOM descendant, but hit-testing is geometric: crossing that
 *    strip fires pointerleave. Closing on a timer rather than immediately is
 *    what lets the cursor make the trip.
 *
 * 2. On a touchscreen, pointerenter fires before the click that follows it, so
 *    an unguarded hover-open would open the panel and the tap would then shut
 *    it again. Hence the pointerType and the (hover: hover) gate.
 */
if (typeof HeaderMenu !== 'undefined') {
  const OPEN_DELAY = 100;
  const CLOSE_DELAY = 200;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  // Only one panel is ever on screen. Tracked here rather than by querying the
  // DOM so that a native click is accounted for the same as a hover.
  let openMenu = null;

  class HeaderNavMenu extends HeaderMenu {
    constructor() {
      super();
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
    }

    disconnectedCallback() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (openMenu === this) openMenu = null;
    }

    onPointerEnter = (event) => {
      if (event.pointerType !== 'mouse' || !finePointer.matches) return;

      clearTimeout(this.closeTimer);
      if (this.mainDetailsToggle.open) return;

      // Sweeping the cursor across the header should not flash the panel open.
      // Once one panel is up, though, moving to the next should feel instant —
      // that is how a menu bar behaves everywhere else.
      this.openTimer = setTimeout(() => this.open(), openMenu ? 0 : OPEN_DELAY);
    };

    onPointerLeave = (event) => {
      if (event.pointerType !== 'mouse') return;

      clearTimeout(this.openTimer);
      if (!this.mainDetailsToggle.open) return;

      this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
    };

    open() {
      this.mainDetailsToggle.setAttribute('open', '');
    }

    onToggle() {
      super.onToggle();

      const isOpen = this.mainDetailsToggle.open;
      this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', isOpen);

      // <details> queues its toggle event as a task rather than dispatching it
      // synchronously, so the panel being closed here reports in after this
      // block has already claimed openMenu. The identity check is what keeps it
      // from clearing its successor.
      if (isOpen) {
        if (openMenu && openMenu !== this) openMenu.close();
        openMenu = this;
      } else if (openMenu === this) {
        openMenu = null;
      }
    }
  }

  customElements.define('header-nav-menu', HeaderNavMenu);
}
/*
 * Desktop panel: the left rail selects which set of cards is on stage.
 *
 * The groups are all in the markup with [hidden] on every one but the first, so
 * the panel is still useful without script. This only moves the flag around.
 */
class HeaderNavPanel extends HTMLElement {
  connectedCallback() {
    this.groups = Array.from(this.querySelectorAll('.hn-panel__group'));

    /*
     * EVERY rail link, not only the ones carrying [data-panel].
     *
     * A row without cards — "Shop All", or any child a merchant has not given
     * nav_card blocks — used to bind no listener at all, so hovering it left
     * the stage showing the PREVIOUS row's products under its name. The frame
     * (1323:17462) draws Shop All deliberately without a chevron: it is a
     * destination, not a category, and the honest stage for it is an empty one.
     *
     * Emptying the stage is free here: the panel's height is set by the rail
     * (measured 224 against the cards' shorter box), so hiding every group
     * changes the panel's height by 0 and nothing moves.
     */
    this.triggers = Array.from(this.querySelectorAll('.hn-panel__rail-link'));

    this.triggers.forEach((trigger) => {
      // pointerenter rather than mouseenter so a pen or touch drag over the rail
      // behaves the same; focusin covers keyboard arrival at the same element.
      trigger.addEventListener('pointerenter', this.onActivate);
      trigger.addEventListener('focusin', this.onActivate);
    });

    // Reflect the initial [hidden] state onto the rail so the first item reads
    // as active from the start rather than only after the first hover.
    const first = this.groups.find((group) => !group.hidden);
    this.defaultTrigger =
      (first && this.triggers.find((t) => t.dataset.panel === first.dataset.panel)) || null;
    if (this.defaultTrigger) this.activate(this.defaultTrigger);

    /*
     * Reopening has to start from the default again. The panel is never
     * re-rendered, so whatever was last hovered persists in the DOM — and once
     * a cardless row can hide every group, that persistence means reopening the
     * menu after brushing "Shop All" would show an empty stage. Resetting on
     * close is invisible, since the panel is already hidden by then.
     */
    this.details = this.closest('details');
    if (this.details && this.defaultTrigger) {
      this.details.addEventListener('toggle', () => {
        if (!this.details.open) this.activate(this.defaultTrigger);
      });
    }
  }

  onActivate = (event) => {
    this.activate(event.currentTarget);
  };

  /*
   * Keyed on the element rather than on [data-panel], because cardless rows
   * have no key — two of them would compare equal and light up together.
   */
  activate(trigger) {
    if (!trigger || trigger === this.activeTrigger) return;
    this.activeTrigger = trigger;

    // undefined on a cardless row, which matches no group and so hides them all.
    const key = trigger.dataset.panel;

    this.groups.forEach((group) => {
      group.hidden = group.dataset.panel !== key;
    });

    this.triggers.forEach((item) => {
      item.classList.toggle('hn-panel__rail-link--active', item === trigger);
    });
  }
}

customElements.define('header-nav-panel', HeaderNavPanel);
